import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { AlertTriangle, Plus, Eye, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const criticidadeLabels = {
    baixa: 'Baixa',
    media: 'Média',
    alta: 'Alta',
    critica: 'Crítica'
};

const statusLabels = {
    aberto: 'Aberto',
    em_tratativa: 'Em Tratativa',
    encerrado: 'Encerrado'
};

const ROOPage = () => {
    const [roos, setRoos] = useState([]);
    const [convoys, setConvoys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedRoo, setSelectedRoo] = useState(null);
    const { hasRole, user } = useAuth();

    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        criticidade: 'media',
        comboio_id: '',
        viagem_id: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [roosRes, convoysRes] = await Promise.all([
                axios.get(`${API}/roos`),
                axios.get(`${API}/convoys`)
            ]);
            setRoos(roosRes.data);
            setConvoys(convoysRes.data);
        } catch (error) {
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            await axios.post(`${API}/roos`, formData);
            toast.success('ROO registrado com sucesso!');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar ROO');
        }
    };

    const handleStatusChange = async (rooId, newStatus) => {
        try {
            await axios.put(`${API}/roos/${rooId}`, { status: newStatus });
            toast.success('Status atualizado!');
            fetchData();
            if (selectedRoo?.id === rooId) {
                setSelectedRoo({ ...selectedRoo, status: newStatus });
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
        }
    };

    const resetForm = () => {
        setFormData({
            titulo: '',
            descricao: '',
            criticidade: 'media',
            comboio_id: '',
            viagem_id: ''
        });
    };

    const openDetail = (roo) => {
        setSelectedRoo(roo);
        setDetailDialogOpen(true);
    };

    const canCreate = hasRole(['admin', 'gestor', 'operador']);
    const canValidate = hasRole(['admin', 'gestor']);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="roo-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Registro de Ocorrência Operacional
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Gerenciamento de ocorrências e desvios operacionais
                    </p>
                </div>
                
                {canCreate && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-roo"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Novo ROO
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white">
                                    Registrar Ocorrência
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Título
                                    </Label>
                                    <Input
                                        data-testid="input-roo-titulo"
                                        value={formData.titulo}
                                        onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                                        required
                                        placeholder="Título da ocorrência"
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Criticidade
                                        </Label>
                                        <Select 
                                            value={formData.criticidade} 
                                            onValueChange={(value) => setFormData({...formData, criticidade: value})}
                                        >
                                            <SelectTrigger data-testid="select-roo-criticidade" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(criticidadeLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Comboio (opcional)
                                        </Label>
                                        <Select 
                                            value={formData.comboio_id || "none"} 
                                            onValueChange={(value) => setFormData({...formData, comboio_id: value === "none" ? "" : value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue placeholder="Selecione..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                <SelectItem value="none">Nenhum</SelectItem>
                                                {convoys.map((convoy) => (
                                                    <SelectItem key={convoy.id} value={convoy.id}>
                                                        {convoy.nome}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Descrição
                                    </Label>
                                    <Textarea
                                        data-testid="input-roo-descricao"
                                        value={formData.descricao}
                                        onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                                        required
                                        placeholder="Descreva a ocorrência em detalhes..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[120px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-roo"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        Registrar
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* ROO Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {roos.length === 0 ? (
                        <div className="p-12 text-center">
                            <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhuma ocorrência registrada</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Código</TableHead>
                                    <TableHead>Título</TableHead>
                                    <TableHead>Criticidade</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Operador</TableHead>
                                    <TableHead>Data</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {roos.map((roo) => (
                                    <TableRow key={roo.id} data-testid={`roo-row-${roo.id}`}>
                                        <TableCell className="font-mono text-cyan-400">
                                            {roo.codigo}
                                        </TableCell>
                                        <TableCell className="text-white">
                                            {roo.titulo}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${roo.criticidade === 'baixa' ? 'bg-slate-800 text-slate-400 border border-slate-700' : ''}
                                                ${roo.criticidade === 'media' ? 'badge-info' : ''}
                                                ${roo.criticidade === 'alta' ? 'badge-warning' : ''}
                                                ${roo.criticidade === 'critica' ? 'badge-danger' : ''}
                                            `}>
                                                {criticidadeLabels[roo.criticidade]}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${roo.status === 'aberto' ? 'badge-warning' : ''}
                                                ${roo.status === 'em_tratativa' ? 'badge-info' : ''}
                                                ${roo.status === 'encerrado' ? 'badge-success' : ''}
                                            `}>
                                                {statusLabels[roo.status]}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-slate-400">
                                            {roo.operador_nome}
                                        </TableCell>
                                        <TableCell className="text-slate-500 text-xs font-mono">
                                            {formatDate(roo.criado_em)}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openDetail(roo)}
                                                className="text-slate-400 hover:text-white hover:bg-slate-800"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <FileText className="w-5 h-5 text-cyan-400" />
                            {selectedRoo?.codigo}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedRoo && (
                        <div className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Título</p>
                                    <p className="text-white mt-1">{selectedRoo.titulo}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Criticidade</p>
                                    <span className={`
                                        inline-block mt-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                        ${selectedRoo.criticidade === 'baixa' ? 'bg-slate-800 text-slate-400 border border-slate-700' : ''}
                                        ${selectedRoo.criticidade === 'media' ? 'badge-info' : ''}
                                        ${selectedRoo.criticidade === 'alta' ? 'badge-warning' : ''}
                                        ${selectedRoo.criticidade === 'critica' ? 'badge-danger' : ''}
                                    `}>
                                        {criticidadeLabels[selectedRoo.criticidade]}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase">Descrição</p>
                                <p className="text-slate-300 mt-1 text-sm">{selectedRoo.descricao}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Status</p>
                                    <p className="text-white mt-1">{statusLabels[selectedRoo.status]}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Operador</p>
                                    <p className="text-white mt-1">{selectedRoo.operador_nome}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Data</p>
                                    <p className="text-white mt-1 text-sm">{formatDate(selectedRoo.criado_em)}</p>
                                </div>
                            </div>

                            {selectedRoo.validado_por && (
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Validado por</p>
                                    <p className="text-emerald-400 mt-1">{selectedRoo.validado_por}</p>
                                </div>
                            )}

                            {/* Status Actions */}
                            {selectedRoo.status !== 'encerrado' && (
                                <div className="border-t border-slate-800 pt-4">
                                    <p className="text-xs text-slate-500 font-mono uppercase mb-3">Alterar Status</p>
                                    <div className="flex gap-2">
                                        {selectedRoo.status === 'aberto' && (
                                            <Button
                                                onClick={() => handleStatusChange(selectedRoo.id, 'em_tratativa')}
                                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm text-xs"
                                            >
                                                Iniciar Tratativa
                                            </Button>
                                        )}
                                        {canValidate && (
                                            <Button
                                                onClick={() => handleStatusChange(selectedRoo.id, 'encerrado')}
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs"
                                            >
                                                Encerrar ROO
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ROOPage;
